import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { FileBarChart2, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = `${window.__BACKEND_URL__}/api`;

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-medium block mb-1" style={{ color: '#A0AEC0' }}>{label}</span>
    {children}
  </label>
);
const sel = 'w-full px-3 py-2 rounded-lg text-sm bg-[#0F1923] border border-[#2D3748] text-white';

// Settings → Branding: how printed / PDF reports are laid out (logo, orientation, charts, footer).
export default function ReportLayoutCard({ token, company }) {
  const { toast } = useToast();
  const b = company?.document_branding || {};
  const [form, setForm] = useState({
    report_logo_position: b.report_logo_position || b.logo_position || 'left',
    report_orientation: b.report_orientation || 'portrait',
    report_show_charts: b.report_show_charts !== false,
    report_show_generated_by: b.report_show_generated_by !== false,
    report_footer_text: b.report_footer_text || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/company/document-branding`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Report layout saved', description: 'Export PDF / Print on the Reports page now uses this layout.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Card className="mb-6" data-testid="report-layout-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><FileBarChart2 className="w-5 h-5" /> Report layout</CardTitle>
        <CardDescription>Used by "Export PDF" and "Print" on every report — logo, colours and fonts come from Document branding above. Ideal for meeting hand-outs.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Logo position">
            <select className={sel} value={form.report_logo_position} onChange={(e) => set('report_logo_position', e.target.value)} data-testid="rl-logo-position">
              <option value="left">Left</option><option value="center">Centre</option><option value="right">Right</option>
            </select>
          </Field>
          <Field label="Page orientation">
            <select className={sel} value={form.report_orientation} onChange={(e) => set('report_orientation', e.target.value)} data-testid="rl-orientation">
              <option value="portrait">Portrait</option><option value="landscape">Landscape (wide tables)</option>
            </select>
          </Field>
          <Field label="Footer text">
            <input className={sel} maxLength={300} value={form.report_footer_text} onChange={(e) => set('report_footer_text', e.target.value)}
              placeholder="e.g. Confidential — prepared for the operations meeting" data-testid="rl-footer" />
          </Field>
        </div>
        <div className="flex flex-wrap gap-6 text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.report_show_charts} onChange={(e) => set('report_show_charts', e.target.checked)} className="accent-sky-500" data-testid="rl-show-charts" />
            <span>Include the chart when one is shown</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.report_show_generated_by} onChange={(e) => set('report_show_generated_by', e.target.checked)} className="accent-sky-500" data-testid="rl-show-by" />
            <span>Show "Prepared by" name</span>
          </label>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#1E88E5] text-white" data-testid="rl-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null} Save report layout
        </Button>
      </CardContent>
    </Card>
  );
}
